import { useQuery } from '@tanstack/react-query';
import { dinheiro } from '../agenda/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { cobrancasApi } from '../api/planos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Barras } from '../graficos/Barras.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Avatar, Botao, Mini, Nota, Vazio } from '../ui/primitivos.jsx';
import { nomeDoMes } from './formatos.js';

/**
 * `#m-kpi` com `data-kpi="inad"` — o drill-down de inadimplência.
 *
 * Tudo vem calculado do servidor, com o escopo do perfil: o valor vencido
 * hoje, a série dos seis meses e os três que estão há mais tempo em atraso.
 * A tela não soma nada.
 *
 * A memória de cálculo é a do protótipo, com uma correção: ele atribui a
 * queda a uma régua de mensagens automáticas, que o produto não tem. Aqui
 * a nota diz o que o número É, não por que ele caiu.
 */
export function ModalDeInadimplencia({
  onFechar,
  onReceber,
}: {
  onFechar: () => void;
  onReceber: (faturaId: string) => void;
}) {
  const { pode } = useSessao();
  const consulta = useQuery({
    queryKey: ['cobrancas', 'inadimplencia'],
    queryFn: () => cobrancasApi.inadimplencia(),
  });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Inadimplência"
      descricao="Faturas vencidas e não pagas"
      rodape={
        <Botao variante="fantasma" onClick={onFechar}>
          Fechar
        </Botao>
      }
    >
      <CorpoDeModal>
        {consulta.isPending && <EsqueletoDeCard linhas={5} />}
        {consulta.isError && (
          <Erro
            tipo={
              consulta.error instanceof ErroDeRede
                ? 'rede'
                : consulta.error instanceof ErroDeApi && consulta.error.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(consulta.error)}
          />
        )}
        {consulta.data && (
          <>
            <Mini
              itens={[
                { valor: dinheiro(consulta.data.valor), rotulo: 'em aberto' },
                {
                  valor: consulta.data.faturas,
                  rotulo: consulta.data.faturas === 1 ? 'fatura' : 'faturas',
                },
                {
                  valor:
                    consulta.data.percentualDaReceita === null
                      ? '—'
                      : `${consulta.data.percentualDaReceita.toLocaleString('pt-BR')}%`,
                  rotulo: 'da receita do mês',
                },
              ]}
            />
            <div>
              <div className="card-head" style={{ marginBottom: 10 }}>
                <h2>Últimos 6 meses</h2>
                <span className="hint">passe o mouse nas barras</span>
              </div>
              <Barras
                cores={['var(--serie-1)']}
                colunas={consulta.data.serie.map((s) => ({
                  x: nomeDoMes(s.mes)
                    .slice(0, 3)
                    .replace(/^./, (c) => c.toUpperCase()),
                  valores: [s.valor],
                  dicas: [dinheiro(s.valor)],
                }))}
              />
            </div>
            <Nota>
              <b>Cálculo.</b> Soma das faturas com vencimento passado e sem pagamento registrado, no
              fuso da arena. Em cada mês, o que estava vencido e sem pagamento no último dia dele;
              no mês corrente, hoje.
            </Nota>
            <div>
              <div className="card-head" style={{ marginBottom: 10 }}>
                <h2>Quem está em atraso</h2>
                <span className="hint">{consulta.data.maiores.length} maiores</span>
              </div>
              {consulta.data.maiores.length === 0 ? (
                <Vazio>Ninguém em atraso.</Vazio>
              ) : (
                <ul className="feed">
                  {consulta.data.maiores.map((d) => (
                    <li key={d.faturaId}>
                      <Avatar nome={d.aluno.nome} />
                      <div>
                        <b>{d.aluno.nome}</b>
                        <small>
                          {d.diasDeAtraso} {d.diasDeAtraso === 1 ? 'dia' : 'dias'} ·{' '}
                          {dinheiro(d.valor)} · {d.plano}
                        </small>
                      </div>
                      {pode('cobrancas', 'registrar_pagamento') && (
                        <div className="rt">
                          <Botao variante="fantasma" pequeno onClick={() => onReceber(d.faturaId)}>
                            Receber
                          </Botao>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
